import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatNyDateTime } from '../../utils/datetime';

const ActivityCard = ({ activity, language = 'pt-BR', onOpenChanges }) => {
  const { tipo, titulo, mensagem, dados_extras = {}, created_at, prioridade, title, messages, recipients_count } = activity;

  const isFomReview = tipo === 'labor_bill_fom_review' || dados_extras?.type === 'labor_bill_fom_review';
  const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Threads de mensagens/tickets antigos: têm `title` + `messages`, sem `tipo`/`titulo`
  // (após "Arrumar registros vazios" recebem tipo='message_thread')
  const isMessageThread = (tipo === 'message_thread' || !tipo) && (Array.isArray(messages) && messages.length > 0);
  const displayTitle = titulo || title || (isMessageThread
    ? (language === 'pt-BR' ? '💬 Mensagem / Ticket' : '💬 Message / Ticket')
    : (language === 'pt-BR' ? '(Sem título)' : '(No title)'));

  const getPriorityBadge = () => {
    switch (prioridade) {
      case 'urgent':
      case 'urgente':
        return <Badge className="bg-red-500 text-white">🚨 Urgente</Badge>;
      case 'high':
      case 'alta':
        return <Badge className="bg-orange-500 text-white">⚠️ Alta</Badge>;
      case 'medium':
      case 'media':
        return <Badge className="bg-blue-500 text-white">ℹ️ Média</Badge>;
      default:
        return <Badge className="bg-gray-500 text-white">📝 Normal</Badge>;
    }
  };

  const getIcon = () => {
    if (tipo?.includes('rental_request')) return '🚛';
    if (tipo?.includes('rental_approved')) return '✅';
    if (tipo?.includes('rental_rejected')) return '❌';
    if (tipo?.includes('problem')) return '⚠️';
    if (tipo?.includes('checkout')) return '🏁';
    if (tipo?.includes('checkin')) return '✅';
    if (tipo?.includes('maintenance_registered')) return '🔧';
    if (tipo?.includes('maintenance_completed')) return '✅';
    if (tipo === 'labor_bill_created') return '📋';
    if (tipo === 'labor_bill_sent') return '📧';
    if (tipo === 'labor_bill_edited') return '✏️';
    if (tipo === 'labor_bill_fom_submitted') return '✅';
    if (tipo === 'labor_bill_fom_review') return dados_extras?.has_changes ? '✏️' : '✅';
    if (tipo === 'labor_bill_fom_sent') return '📤';
    if (tipo === 'payroll_report_saved') return '📊';
    if (tipo === 'checkout_requested') return '🏁';
    if (tipo === 'rental_approved_admin') return '✅';
    if (tipo === 'rental_rejected_admin') return '❌';
    if (tipo === 'message_sent') return '📨';
    if (tipo === 'ticket' || tipo === 'ticket_created') return '🎫';
    if (tipo === 'message_thread' || isMessageThread) return '💬';
    if (tipo === 'customer_review') return '⭐';
    return '📌';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatNyDateTime(dateStr);
  };

  // Renderizar detalhes baseado no tipo
  const renderDetails = () => {
    // REVISÃO DO FOM (Supervisor) - com clique para abrir modal de alterações
    if (isFomReview) {
      const chg = dados_extras.fom_changes || [];
      return (
        <div className="text-sm space-y-1">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '✍️ Assinado por:' : '✍️ Signed by:'}</span>{' '}
            <strong>{dados_extras.signer_name || 'Supervisor'}</strong>
          </div>
          {dados_extras.order_number ? (
            <div><span className="text-gray-500">{language === 'pt-BR' ? '📦 Ordem:' : '📦 Order:'}</span> <strong>{dados_extras.order_number}</strong></div>
          ) : null}
          {dados_extras.has_changes ? (
            <div>
              <span className="text-gray-500">{language === 'pt-BR' ? '💰 Total:' : '💰 Total:'}</span>{' '}
              <span className="line-through text-gray-400">{fmtUsd(dados_extras.old_total)}</span> →{' '}
              <strong className="text-emerald-700">{fmtUsd(dados_extras.new_total)}</strong>
              <span className="text-gray-500"> · </span>
              <strong>{chg.length}</strong> {language === 'pt-BR' ? 'alteração(ões)' : 'change(s)'}
            </div>
          ) : (
            <div>
              <span className="text-gray-500">{language === 'pt-BR' ? '💰 Total:' : '💰 Total:'}</span>{' '}
              <strong className="text-emerald-700">{fmtUsd(dados_extras.new_total)}</strong>
              <span className="text-gray-500"> · {language === 'pt-BR' ? 'nada foi alterado' : 'no changes'}</span>
            </div>
          )}
          {dados_extras.has_changes ? (
            <div className="text-emerald-600 underline text-xs mt-1" data-testid="activity-fom-review-open">
              {language === 'pt-BR' ? 'ver detalhes das alterações' : 'view change details'}
            </div>
          ) : null}
        </div>
      );
    }
    // SISTEMA DE TRUCKS - Solicitação
    if (tipo === 'rental_request') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👥 Equipe:' : '👥 Team:'}</span>
            <p className="font-semibold">{dados_extras.team_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📏 KM:' : '📏 KM:'}</span>
            <p className="font-semibold">{dados_extras.truck_km?.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Início:' : '📅 Start:'}</span>
            <p className="font-semibold">{dados_extras.data_inicio}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🏁 Fim:' : '🏁 End:'}</span>
            <p className="font-semibold">{dados_extras.data_fim}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-500">{language === 'pt-BR' ? '📝 Propósito:' : '📝 Purpose:'}</span>
            <p className="font-semibold">{dados_extras.proposito}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE TRUCKS - Aprovação
    if (tipo === 'rental_approved') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '✅ Aprovado por:' : '✅ Approved by:'}</span>
            <p className="font-semibold">{dados_extras.approved_by || 'ADMIN'}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 Para:' : '👤 For:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Início:' : '📅 Start:'}</span>
            <p className="font-semibold">{dados_extras.data_inicio}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE TRUCKS - Rejeição
    if (tipo === 'rental_rejected') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '❌ Rejeitado por:' : '❌ Rejected by:'}</span>
            <p className="font-semibold">ADMIN</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-500">{language === 'pt-BR' ? '📝 Motivo:' : '📝 Reason:'}</span>
            <p className="font-semibold text-red-600">{dados_extras.motivo}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE TRUCKS - Check-in
    if (tipo === 'checkin_completed') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📏 KM Inicial:' : '📏 Initial KM:'}</span>
            <p className="font-semibold">{dados_extras.km_inicial?.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '⛽ Combustível:' : '⛽ Fuel:'}</span>
            <p className="font-semibold">{dados_extras.combustivel}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📸 Fotos:' : '📸 Photos:'}</span>
            <p className="font-semibold">{dados_extras.fotos_count || 0} {language === 'pt-BR' ? 'anexadas' : 'attached'}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '✍️ Assinatura:' : '✍️ Signature:'}</span>
            <p className="font-semibold text-green-600">✅ {language === 'pt-BR' ? 'Registrada' : 'Registered'}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE TRUCKS - Problema
    if (tipo === 'problem_reported') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📝 Problema:' : '📝 Problem:'}</span>
            <p className="font-semibold text-red-600">{dados_extras.problem_title}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📏 KM Atual:' : '📏 Current KM:'}</span>
            <p className="font-semibold">{dados_extras.km_atual?.toLocaleString()}</p>
          </div>
          {dados_extras.localizacao && (
            <div className="md:col-span-2">
              <span className="text-gray-500">{language === 'pt-BR' ? '📍 Localização:' : '📍 Location:'}</span>
              <p className="font-semibold">{dados_extras.localizacao}</p>
            </div>
          )}
          {dados_extras.custo_adicional > 0 && (
            <div>
              <span className="text-gray-500">{language === 'pt-BR' ? '💰 Custo:' : '💰 Cost:'}</span>
              <p className="font-semibold">${dados_extras.custo_adicional?.toFixed(2)}</p>
            </div>
          )}
        </div>
      );
    }

    // SISTEMA DE TRUCKS - Checkout Completo
    if (tipo === 'rental_completed' || tipo === 'checkout_completed') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Período:' : '📅 Period:'}</span>
            <p className="font-semibold">{dados_extras.check_in_date} - {dados_extras.check_out_date}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📆 Dias:' : '📆 Days:'}</span>
            <p className="font-semibold">{dados_extras.dias_alugados}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📏 KM:' : '📏 KM:'}</span>
            <p className="font-semibold">{dados_extras.km_inicial?.toLocaleString()} → {dados_extras.km_final?.toLocaleString()} ({(dados_extras.km_final - dados_extras.km_inicial)?.toLocaleString()} km)</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💰 Valor:' : '💰 Amount:'}</span>
            <p className="font-semibold text-green-600">${dados_extras.valor_final?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💳 Pagamento:' : '💳 Payment:'}</span>
            <p className="font-semibold">{dados_extras.metodo_pagamento}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE LABOR BILLS - Criado
    if (tipo === 'labor_bill_created') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👤 PM:' : '👤 PM:'}</span>
            <p className="font-semibold">{dados_extras.pm_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👥 Equipe:' : '👥 Team:'}</span>
            <p className="font-semibold">{dados_extras.team_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📦 Order:' : '📦 Order:'}</span>
            <p className="font-semibold">#{dados_extras.order_number}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🏠 Cliente:' : '🏠 Customer:'}</span>
            <p className="font-semibold">{dados_extras.job_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Install:' : '📅 Install:'}</span>
            <p className="font-semibold">{dados_extras.install_date}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💰 Valor:' : '💰 Amount:'}</span>
            <p className="font-semibold text-green-600">${dados_extras.grand_total?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🪟 Windows:' : '🪟 Windows:'}</span>
            <p className="font-semibold">{dados_extras.total_windows}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚪 Doors:' : '🚪 Doors:'}</span>
            <p className="font-semibold">{dados_extras.total_doors}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE LABOR BILLS - Enviado
    if (tipo === 'labor_bill_sent') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📨 Enviado por:' : '📨 Sent by:'}</span>
            <p className="font-semibold">{dados_extras.pm_name || dados_extras.sent_by}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📧 Para:' : '📧 To:'}</span>
            <p className="font-semibold">{dados_extras.recipient_email}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📦 Order:' : '📦 Order:'}</span>
            <p className="font-semibold">#{dados_extras.order_number}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🏠 Cliente:' : '🏠 Customer:'}</span>
            <p className="font-semibold">{dados_extras.job_name}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💰 Valor:' : '💰 Amount:'}</span>
            <p className="font-semibold text-green-600">${dados_extras.grand_total?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📎 Anexos:' : '📎 Attachments:'}</span>
            <p className="font-semibold">{dados_extras.attachments_count || 1}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE MENSAGENS - Enviada
    if (tipo === 'message_sent') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📨 De:' : '📨 From:'}</span>
            <p className="font-semibold">{dados_extras.sent_by || 'ADMIN'}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👥 Para:' : '👥 To:'}</span>
            <p className="font-semibold">
              {dados_extras.target_user === 'ALL' 
                ? `${language === 'pt-BR' ? 'Todos' : 'All'} (${dados_extras.recipients_count || 0})` 
                : dados_extras.target_user}
            </p>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-500">{language === 'pt-BR' ? '💬 Prévia:' : '💬 Preview:'}</span>
            <p className="font-semibold italic text-gray-700">"{dados_extras.message_preview}"</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE MANUTENÇÃO - Registrada
    if (tipo === 'maintenance_registered') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🔧 Tipo:' : '🔧 Type:'}</span>
            <p className="font-semibold capitalize">{dados_extras.tipo_manutencao}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-500">{language === 'pt-BR' ? '📝 Descrição:' : '📝 Description:'}</span>
            <p className="font-semibold">{dados_extras.descricao}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Início:' : '📅 Start:'}</span>
            <p className="font-semibold">{dados_extras.data_inicio}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🏁 Previsão:' : '🏁 Expected:'}</span>
            <p className="font-semibold">{dados_extras.data_fim_prevista || (language === 'pt-BR' ? 'Não definida' : 'Not set')}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👷 Responsável:' : '👷 Responsible:'}</span>
            <p className="font-semibold">{dados_extras.responsavel}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💰 Custo:' : '💰 Cost:'}</span>
            <p className="font-semibold text-orange-600">${dados_extras.custo_estimado?.toFixed(2)}</p>
          </div>
        </div>
      );
    }

    // SISTEMA DE MANUTENÇÃO - Concluída
    if (tipo === 'maintenance_completed') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🚛 Caminhão:' : '🚛 Truck:'}</span>
            <p className="font-semibold">{dados_extras.truck_model} ({dados_extras.truck_plate})</p>
            {dados_extras.truck_vin && (
              <p className="text-xs font-mono text-gray-500">🔖 {dados_extras.truck_vin}</p>
            )}
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '🔧 Tipo:' : '🔧 Type:'}</span>
            <p className="font-semibold capitalize">{dados_extras.tipo_manutencao}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📅 Período:' : '📅 Period:'}</span>
            <p className="font-semibold">{dados_extras.data_inicio} - {dados_extras.data_fim_real}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '👷 Responsável:' : '👷 Responsible:'}</span>
            <p className="font-semibold">{dados_extras.responsavel}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '💰 Custo:' : '💰 Cost:'}</span>
            <p className="font-semibold text-green-600">${dados_extras.custo_final?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-gray-500">{language === 'pt-BR' ? '📊 Total Acumulado:' : '📊 Total Accumulated:'}</span>
            <p className="font-semibold">${dados_extras.custo_total_acumulado?.toFixed(2)}</p>
          </div>
        </div>
      );
    }

    // Threads de mensagens / tickets (estrutura antiga: title + messages, sem tipo)
    if (isMessageThread) {
      const last = messages[messages.length - 1] || {};
      const preview = String(last.text || last.message || '').slice(0, 180);
      return (
        <div className="text-sm text-gray-700">
          <p className="text-xs text-gray-500 mb-1">
            💬 {messages.length} {language === 'pt-BR' ? 'mensagem(ns)' : 'message(s)'}
          </p>
          {preview ? (
            <p>
              <span className="font-semibold">{last.author || '—'}:</span>{' '}
              <span className="italic">"{preview}"</span>
            </p>
          ) : (
            <p className="italic text-gray-400">
              {language === 'pt-BR' ? '(sem conteúdo)' : '(no content)'}
            </p>
          )}
        </div>
      );
    }

    // Fallback para outros tipos
    return (
      <div className="text-sm text-gray-600">
        <p>{mensagem || (language === 'pt-BR' ? '(sem detalhes)' : '(no details)')}</p>
      </div>
    );
  };

  return (
    <Card
      className={`mb-3 hover:shadow-md transition-shadow ${isFomReview && dados_extras.has_changes ? 'cursor-pointer border-emerald-200' : ''}`}
      onClick={isFomReview && dados_extras.has_changes && onOpenChanges ? () => onOpenChanges(dados_extras) : undefined}
      data-testid={isFomReview ? 'activity-fom-review-row' : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Ícone */}
          <div className="text-3xl flex-shrink-0">{getIcon()}</div>
          
          {/* Conteúdo */}
          <div className="flex-1 min-w-0">
            {/* Cabeçalho */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{displayTitle}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(created_at)}
                </p>
                {(() => {
                  const team = dados_extras?.pm_username || activity.pm_user || activity.recipient_username;
                  const client = dados_extras?.job_name || dados_extras?.client_name;
                  const orderNo = dados_extras?.order_number;
                  if (!team && !client && !orderNo) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="activity-meta-badges">
                      {team && (
                        <Badge className="bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap" data-testid="activity-team-badge">
                          🏷️ {team}
                        </Badge>
                      )}
                      {client && (
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap" data-testid="activity-client-badge">
                          👤 {client}
                        </Badge>
                      )}
                      {orderNo && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap" data-testid="activity-order-badge">
                          #️⃣ {orderNo}
                        </Badge>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {recipients_count > 1 && (
                  <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 whitespace-nowrap" data-testid="activity-recipients-badge">
                    👥 {language === 'pt-BR' ? `enviado a ${recipients_count} usuários` : `sent to ${recipients_count} users`}
                  </Badge>
                )}
                {prioridade && getPriorityBadge()}
              </div>
            </div>
            
            {/* Detalhes */}
            <div className="mt-3 bg-gray-50 p-3 rounded-lg">
              {renderDetails()}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityCard;
